import {
    type AutomationTaskEdit,
    AutomationTaskStatus,
    type OptionValueType,
    type Action as TypeAction,
    type Trigger as TypeTrigger,
} from '../types/types';

// The updateAction/updateTrigger might not be needed when editing is done in the drawer.
// Leaving them in for now just in case we end up needing them still.
export type TaskEditAction =
    | { type: 'setTask'; payload: { task: AutomationTaskEdit } }
    | { type: 'updateName'; payload: { displayName: string } }
    | { type: 'updateActiveState'; payload: { isActive: boolean } }
    | { type: 'addTrigger'; payload: { trigger: TypeTrigger } }
    | { type: 'updateTrigger'; payload: { optionId: string; optionValue: OptionValueType } }
    | { type: 'deleteTrigger'; payload: null }
    | { type: 'addAction'; payload: { action: TypeAction; index: number } }
    | {
          type: 'updateAction';
          payload: { index: number; optionId: string; optionValue: OptionValueType };
      }
    | { type: 'deleteAction'; payload: { index: number } }
    | { type: 'editAction'; payload: { index: number; action: TypeAction } };

export const taskReducer = (
    task: AutomationTaskEdit | null,
    action: TaskEditAction,
): AutomationTaskEdit | null => {
    const { type, payload } = action;

    if (type === 'setTask') {
        return payload.task;
    }

    if (task === null) {
        throw new Error('task has not been initialized, only `setTask` can be called');
    }

    switch (type) {
        case 'updateName':
            return { ...task, displayName: payload.displayName };
        case 'updateActiveState':
            return {
                ...task,
                status: AutomationTaskStatus.ModifiedTrigger,
                isActive: payload.isActive,
            };
        case 'addTrigger': {
            return {
                ...task,
                trigger: payload.trigger,
                status:
                    'id' in task
                        ? AutomationTaskStatus.ModifiedTrigger
                        : AutomationTaskStatus.Modified,
            };
        }
        case 'updateTrigger': {
            const { optionId, optionValue } = payload;
            if (!task.trigger) {
                return task;
            }
            return {
                ...task,
                trigger: {
                    ...task.trigger,
                    values: {
                        ...task.trigger.values,
                        [optionId]: optionValue,
                    },
                },
                status:
                    'id' in task
                        ? AutomationTaskStatus.ModifiedTrigger
                        : AutomationTaskStatus.Modified,
            };
        }
        case 'deleteTrigger':
            return { ...task, trigger: null };
        case 'addAction': {
            const newActions = [...task.actions, payload.action];
            return { ...task, actions: newActions };
        }
        case 'updateAction': {
            const { index, optionId, optionValue } = payload;

            const updatedAction = {
                ...task.actions[index],
                configurations: {
                    ...task.actions[index].values,
                    [optionId]: optionValue,
                },
            };

            const updatedActions = task.actions.map((action, indexToCheck) => {
                if (index === indexToCheck) {
                    return updatedAction;
                }
                return action;
            });

            return { ...task, actions: updatedActions };
        }
        case 'deleteAction': {
            const updatedActions = task.actions.filter((_, index) => index !== payload.index);
            return { ...task, actions: updatedActions };
        }
        case 'editAction': {
            if (payload.index === undefined) {
                return task;
            }
            const updatedActions = [...task.actions];
            updatedActions[payload.index] = payload.action;
            return { ...task, actions: updatedActions };
        }
    }
};
